import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportErrorBoundary } from "@rook/shared/lib/errorReporter";

/**
 * Root React error boundary. Wraps the entire app so any uncaught
 * exception during render / commit / effect cleanup lands here
 * instead of leaving the SPA blank.
 *
 * Two responsibilities:
 *   1. Show a friendly fallback UI carrying a short reference id the
 *      customer can quote to support.
 *   2. Fire-and-forget POST a sanitized payload (PII redacted, length
 *      truncated) to `/api/observability/error` so the team sees the
 *      stack + route + browser even when the customer never reports it.
 *
 * Why a class component: React error boundaries can only be class
 * components — `componentDidCatch` and `getDerivedStateFromError` are
 * not exposed in the hooks API as of React 19. Keep this one tiny;
 * the moment we need hooks, we can wrap a hooks component below.
 */

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  reference: string;
};

const newReference = (): string => {
  // First 8 hex chars of a UUID — enough to be quotable + globally
  // unique within the scope that matters (one user's session). We
  // use `crypto.randomUUID()` (Web Crypto, all evergreen browsers)
  // rather than `Math.random()` so the value isn't pseudo-random
  // (sonarjs flags Math.random for any non-display use). The
  // reference appears in CloudWatch logs alongside the stack, so
  // collision avoidance > display elegance.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().slice(0, 8);
  }
  // Fallback for environments without Web Crypto (e.g. older test
  // runners). Hex-time is still globally unique enough at the
  // millisecond scale of a boundary trigger.
  return Date.now().toString(16).slice(-8);
};

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reference: "" };

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true, reference: newReference() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportErrorBoundary({
      reference: this.state.reference,
      errorName: error.name,
      errorMessage: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      route:
        typeof window === "undefined"
          ? "(unknown)"
          : window.location.pathname,
      userAgent:
        typeof navigator === "undefined" ? "(unknown)" : navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }

  handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-rook-background p-6">
        <div className="max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
          <h1 className="text-lg font-semibold tracking-tight text-foreground">
            Something went wrong
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            We hit an unexpected error. The team has been notified
            automatically. If you need to follow up, please reference:
          </p>
          <code className="mt-3 inline-block rounded bg-white/5 px-3 py-1.5 font-mono text-xs text-rook-light">
            {this.state.reference}
          </code>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 w-full rounded-lg bg-rook px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rook/90"
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
